import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BillingSummary from "../BillingSummary";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import {
  AxiosInstance,
  CGST_RATE,
  SGST_RATE,
  RAZORPAY_SCRIPT_SRC,
  RAZORPAY_KEY_ID,
} from "../../../../services/Api";
import styles from "../Pricing.module.css";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    if (document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`)) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const SummaryItem = ({ label, value }) => (
  <div className={styles.summaryItem}>
    <span className={styles.summaryLabel}>{label}</span>
    <span className={styles.summaryValue}>{value || "-"}</span>
  </div>
);

const Step4ReviewPay = ({
  schoolDetails,
  onFieldChange,
  addOns,
  selectedAddOnIds,
  plan,
  slab,
  onBack,
}) => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [pendingPurchaseOrderId, setPendingPurchaseOrderId] = useState(null);
  // NEW: controls the "resume previous transaction" confirmation popup
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);

  const adminFirstName = schoolDetails.sameAsOrganization
    ? schoolDetails.firstName
    : schoolDetails.adminFirstName;
  const adminLastName = schoolDetails.sameAsOrganization
    ? schoolDetails.lastName
    : schoolDetails.adminLastName;
  const adminEmail = schoolDetails.sameAsOrganization
    ? schoolDetails.email
    : schoolDetails.adminEmail;
  const adminPhone = schoolDetails.sameAsOrganization
    ? schoolDetails.phone
    : schoolDetails.adminPhone;

  const selectedAddOns = addOns.filter((addOn) =>
    selectedAddOnIds.includes(addOn.id),
  );
  const planAmount = Number(slab?.planPrice) || 0;
  const addOnsAmount = selectedAddOns.reduce(
    (sum, addOn) => sum + (Number(addOn.price) || 0),
    0,
  );
  const subtotal = planAmount + addOnsAmount;
  const cgstAmount = Math.round((subtotal * CGST_RATE) / 100);
  const sgstAmount = Math.round((subtotal * SGST_RATE) / 100);
  const gstAmount = cgstAmount + sgstAmount;
  const totalAmount = subtotal + gstAmount;

  const buildPurchasePayload = () => {
    return {
      ...(pendingPurchaseOrderId
        ? { purchase_order_id: pendingPurchaseOrderId }
        : {}),
      plan_id: plan?.id,
      slab_id: plan?.slabId,
      range_no: slab?.rangeNo,
      max_users: slab?.maxUsers,
      plan_price: planAmount,
      amc_price: Number(slab?.amcPrice) || 0,
      cgst: cgstAmount,
      sgst: sgstAmount,
      total_amount: totalAmount,
      created_by: schoolDetails.email,
      add_ons: selectedAddOns.map((addOn) => ({
        addon_configuration_id: addOn.id,
        addon_name: addOn.name,
        addon_price: addOn.price,
      })),
      school_details: [
        {
          organization_first_name: schoolDetails.firstName,
          organization_last_name: schoolDetails.lastName,
          organization_phone: schoolDetails.phone,
          organization_email: schoolDetails.email,
          school_name: schoolDetails.schoolName,
          school_email: schoolDetails.email,
          school_board: schoolDetails.schoolBoard?.value || "",
          school_category: schoolDetails.schoolCategory?.value || "",
          city: schoolDetails.city?.label || "",
          state: schoolDetails.state?.value || "",
          pincode: schoolDetails.pincode,
          school_address: schoolDetails.address,
          admin_first_name: adminFirstName,
          admin_last_name: adminLastName,
          admin_email: adminEmail,
          admin_phone: adminPhone,
          short_name: schoolDetails.shortNameUrl,
          sub_domain_url: schoolDetails.shortNameUrl,
          gst_number: schoolDetails.gstin,
          udise_no: schoolDetails.udiseCode,
        },
      ],
    };
  };

  // ==== Existing payment flow — UNCHANGED, only extracted into its own fn ====
  const proceedWithPayment = async () => {
    setPaymentError("");
    setProcessing(true);

    try {
      // 1. Create purchase order
      const purchaseResponse = await AxiosInstance.post(
        "saas/purchase/create",
        buildPurchasePayload(),
      );

      const purchaseOrderId = purchaseResponse.data?.data?.purchase_order_id;
      const createdBy = purchaseResponse.data?.data?.created_by ?? 1;

      if (!purchaseResponse.data?.status || !purchaseOrderId) {
        throw new Error(
          purchaseResponse.data?.message || "Failed to create purchase order",
        );
      }

      // 2. Create Razorpay order
      const orderResponse = await AxiosInstance.post("saas/create-order", {
        purchase_order_id: purchaseOrderId,
        created_by: createdBy,
      });

      const razorpayOrderId =
        orderResponse.data?.data?.razorpay_order_id ||
        orderResponse.data?.razorpay_order_id;

      if (!orderResponse.data?.status || !razorpayOrderId) {
        throw new Error(
          orderResponse.data?.message || "Failed to create payment order",
        );
      }

      // 3. Open Razorpay checkout
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Unable to load Razorpay checkout. Please try again.");
      }

      // 4. Verify and complete the payment — called for both a successful
      // and a failed Razorpay attempt, so the backend always records the
      // outcome against the order.
      const completePurchase = async (transactionId) => {
        try {
          const verifyResponse = await AxiosInstance.post("saas/purchase", {
            razorpay_order_id: razorpayOrderId,
            transaction_id: transactionId,
          });

          if (verifyResponse.status === 200 && verifyResponse.data?.status) {
            navigate("/pricing/payment-success");
          } else {
            navigate("/pricing/payment-failed");
          }
        } catch (verifyError) {
          navigate("/pricing/payment-failed");
        } finally {
          setProcessing(false);
        }
      };

      const razorpay = new window.Razorpay({
        key: RAZORPAY_KEY_ID,
        order_id: razorpayOrderId,
        name: "iFillip",
        description: `${plan?.planName || "Plan"} subscription`,
        prefill: {
          name: `${schoolDetails.firstName || ""} ${
            schoolDetails.lastName || ""
          }`.trim(),
          email: schoolDetails.email,
          contact: schoolDetails.phone,
        },
        theme: { color: "#4F6BFF" },
        handler: (razorpayResponse) => {
          completePurchase(razorpayResponse.razorpay_payment_id);
        },
        modal: {
          ondismiss: () => {
            // User closed the popup without paying — remember this order
            // so the next "Pay Now" reuses it instead of creating a new one.
            setPendingPurchaseOrderId(purchaseOrderId);
            setProcessing(false);
          },
        },
      });

      razorpay.on("payment.failed", (failedResponse) => {
        completePurchase(failedResponse.error?.metadata?.payment_id || "");
      });

      razorpay.open();
    } catch (error) {
      setProcessing(false);
      setPaymentError(
        error.response?.data?.message ||
          error.message ||
          "Payment could not be started. Please try again.",
      );
    }
  };
  // ==== End of unchanged payment flow ====

  // Entry point for the "Pay Now" button.
  // If a previous transaction is already pending, ask the user to confirm
  // that the existing transaction will be updated (instead of creating a
  // new one). Otherwise proceed with the payment flow as-is.
  const handlePayNow = () => {
    if (pendingPurchaseOrderId) {
      setShowResumeConfirm(true);
      return;
    }
    proceedWithPayment();
  };

  const handleConfirmResume = () => {
    setShowResumeConfirm(false);
    proceedWithPayment();
  };

  const handleCancelResume = () => {
    setShowResumeConfirm(false);
  };

  return (
    <div>
      <PopupModal
        open={Boolean(paymentError)}
        title="Failed"
        message={paymentError}
        onOk={() => setPaymentError("")}
        onClose={() => setPaymentError("")}
      />

      {/* NEW: Resume-existing-transaction confirmation */}
      <PopupModal
        open={showResumeConfirm}
        title="Previous Transaction Found"
        message="A previous transaction was already initiated for this payment. The last transaction will be updated instead of creating a new transaction."
        okLabel="OK, Continue"
        cancelLabel="Cancel"
        showCancel
        onOk={handleConfirmResume}
        onCancel={handleCancelResume}
        onClose={handleCancelResume}
      />

      <h2 className={`heading ${styles.stepHeading}`}>
        Complete <span className="gradientText">Your Purchase</span>
      </h2>
      <p className={styles.stepSubtitle}>
        Review filled details and get started with iFillip.
      </p>

      <div className={styles.stepLayout}>
        <div className={styles.formColumn}>
          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>Account Details</h3>
            <div className={styles.summaryGrid}>
              <SummaryItem label="First Name" value={schoolDetails.firstName} />
              <SummaryItem label="Last Name" value={schoolDetails.lastName} />
              <SummaryItem label="Phone Number" value={schoolDetails.phone} />
              <SummaryItem label="Email Address" value={schoolDetails.email} />
            </div>
          </div>

          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>School Details</h3>
            <div className={styles.summaryGrid}>
              <SummaryItem
                label="School Name"
                value={schoolDetails.schoolName}
              />
              <SummaryItem
                label="School Board"
                value={schoolDetails.schoolBoard?.label}
              />
              <SummaryItem
                label="School Category"
                value={schoolDetails.schoolCategory?.label}
              />
              <SummaryItem label="City" value={schoolDetails.city?.label} />
              <SummaryItem label="State" value={schoolDetails.state?.label} />
              <SummaryItem label="Pincode" value={schoolDetails.pincode} />
              <SummaryItem
                label="UDISE Code"
                value={schoolDetails.udiseCode}
              />
              <div className={styles.formFullRow}>
                <SummaryItem
                  label="School Address"
                  value={schoolDetails.address}
                />
              </div>
            </div>
          </div>

          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>
              School Admin Contact Details
            </h3>
            <div className={styles.summaryGrid}>
              <SummaryItem label="First Name" value={adminFirstName} />
              <SummaryItem label="Last Name" value={adminLastName} />
              <SummaryItem label="Email Address" value={adminEmail} />
              <SummaryItem label="Phone Number" value={adminPhone} />
            </div>
          </div>

          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>Short Name URL</h3>
            <div className={styles.summaryGrid}>
              <SummaryItem
                label="Short Name URL"
                value={schoolDetails.shortNameUrl}
              />
            </div>
          </div>

          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>Billing Information</h3>
            <div className={styles.summaryGrid}>
              {/* Optional in step 3 — SummaryItem renders "-" when left blank */}
              <SummaryItem
                label="GSTIN (Optional)"
                value={schoolDetails.gstin}
              />
            </div>
          </div>
        </div>

        <BillingSummary
          plan={plan}
          slab={slab}
          addOns={addOns}
          selectedAddOnIds={selectedAddOnIds}
          showAgreement
          agreementChecked={schoolDetails.agreedToTerms}
          onAgreementChange={(checked) =>
            onFieldChange("agreedToTerms", checked)
          }
          showSecureBadge
          primaryLabel={processing ? "Processing..." : "Pay Now"}
          appendTotalToPrimaryLabel={!processing}
          primaryDisabled={!schoolDetails.agreedToTerms}
          primaryLoading={processing}
          onPrimary={handlePayNow}
          secondaryLabel="Back to School Details"
          onSecondary={onBack}
          secondaryDisabled={processing}
        />
      </div>
    </div>
  );
};

export default Step4ReviewPay;